import { LoadingIndicator, YesNoDialog } from '@ygtechnologies/bygr-frontend-component-library';
import React, { useEffect, useState } from 'react';
import theme from './ProjectModules.module.scss';
import { BygrModule, BygrProject } from '@/types';
import {
    FdvTransferResult,
    getAvailableDocumentCategories,
    setDocumentCategoryHierarchy,
    setModule,
    transfereFDVFromAmToCM as transferFDVFromAmToCm,
} from '@/services/projects';
import ProjectModulesUnitsModal from './ProjectModulesUnitsModal';
import ProjectSelectCategoriesModal from './ProjectSelectCategoriesModal';
import { useAsyncState } from '@/utils/hooks';
import { extractErrorMessage } from '@/utils/ajax';
import { AlertBanner, Card, SlideToggle, Button } from '@ygtechnologies/rubus-ui';

interface ProjectModulesProps {
    project: BygrProject;
    reloadProject(): void;
}

/**
 * What the FDV transfer did. It matches nothing at all when a project has no FDV attached to the
 * products its buyers ordered, so an empty run needs to say why rather than just look successful.
 */
const FdvTransferSummary: React.FC<{ result: FdvTransferResult }> = ({ result }) => {
    const nothingToTransfer = result.documentsExpected === 0;
    const alreadyDone = !nothingToTransfer && result.documentsCreated === 0;

    if (nothingToTransfer) {
        return (
            <AlertBanner type="warning">
                <b>Nothing to transfer.</b>{' '}
                {result.pdfFilesOnOrderedProducts === 0
                    ? 'None of the products ordered in this project have a PDF attached.'
                    : 'The PDFs on this project’s ordered products are not reachable from any signed option order.'}
                <dl className={theme.transferResult}>
                    <dt>Signed orders</dt>
                    <dd>{result.signedOrders}</dd>
                    <dt>Products ordered</dt>
                    <dd>{result.orderedProducts}</dd>
                    <dt>Files on those products</dt>
                    <dd>{result.filesOnOrderedProducts}</dd>
                    <dt>Of those, PDFs</dt>
                    <dd>{result.pdfFilesOnOrderedProducts}</dd>
                </dl>
                <p className={theme.transferResultNote}>
                    Only PDFs attached to the ordered products are transferred. Attach them to those products in the CM
                    catalog, then run this again.
                </p>
            </AlertBanner>
        );
    }

    return (
        <AlertBanner type={alreadyDone ? 'info' : 'success'}>
            {alreadyDone ? (
                <>
                    <b>Already up to date.</b> All {result.documentsExpected} documents were transferred previously.
                </>
            ) : (
                <>
                    <b>
                        Transferred {result.documentsCreated} of {result.documentsExpected} documents
                    </b>{' '}
                    to {result.unitsCovered} units.
                </>
            )}
            <dl className={theme.transferResult}>
                <dt>Documents created</dt>
                <dd>{result.documentsCreated}</dd>
                <dt>Documents already in AM</dt>
                <dd>{result.documentsAlreadyPresent}</dd>
                <dt>Unit links created</dt>
                <dd>{result.unitLinksCreated}</dd>
                <dt>Unit links already in place</dt>
                <dd>{result.unitLinksAlreadyPresent}</dd>
                <dt>Room tags added</dt>
                <dd>{result.roomTagsCreated}</dd>
            </dl>
            {result.documentsWithoutRoom > 0 && (
                <p className={theme.transferResultNote}>
                    {result.documentsWithoutRoom} of the new documents got no room tag because the orders behind them
                    carry no room. Set their rooms by hand in AM.
                </p>
            )}
            {result.documentsSkippedNoUploader > 0 && (
                <p className={theme.transferResultNote}>
                    {result.documentsSkippedNoUploader} documents were skipped because neither their order nor its option
                    group has a creating user. Transferring them would break the unit&apos;s document list in AM.
                </p>
            )}
        </AlertBanner>
    );
};

const ProjectModules: React.FC<ProjectModulesProps> = ({ project, reloadProject }) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const [showDialog, setShowDialog] = useState(false);
    const [showDialogCM, setShowDialogCM] = useState(false);
    const [showDialogQM, setShowDialogQM] = useState(false);
    const [showDialogSales, setShowDialogSales] = useState(false);
    const [showDialogFDV, setShowDialogFDV] = useState(false);
    const [fdvResult, setFdvResult] = useState<FdvTransferResult | null>(null);
    const [fdvError, setFdvError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [moduleError, setModuleError] = useState<string | null>(null);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showSetCategoryModal, setShowSetCategoryModal] = useState(false);
    const [availableDocumentCategories, setAvalibleDocumentCategories] = useAsyncState(
        () => getAvailableDocumentCategories(project.id),
        [],
        null
    );

    // ProjectDetails re-renders rather than remounts when the route's projectId changes, so without
    // this the previous project's transfer result stays on screen under a different project's name.
    useEffect(() => {
        setFdvResult(null);
        setFdvError(null);
    }, [project.id]);

    const hasModule = (module: BygrModule) => module === (project.sellerModules & module);

    const toggleModule = async (module: BygrModule) => {
        const moduleIsActive = hasModule(module);
        if (
            module === BygrModule.Aftermarket &&
            !moduleIsActive &&
            availableDocumentCategories &&
            !availableDocumentCategories.hasLinkedDocuments
        ) {
            setShowCategoryModal(true);
        } else {
            await doSetModule(module);
        }
    };
    // Reloads whatever the outcome, so the toggles never show a state the server did not save.
    const doSetModule = async (module: BygrModule): Promise<boolean> => {
        const moduleIsActive = hasModule(module);
        setModuleError(null);
        try {
            await setModule(project.id, { isActive: !moduleIsActive, module });
            return true;
        } catch (e) {
            setModuleError((await extractErrorMessage(e)) ?? 'Changing the module failed. The project has been reloaded.');
            return false;
        } finally {
            reloadProject();
        }
    };
    const transfereFDV = async () => {
        setShowDialogFDV(false);
        setLoading(true);
        setFdvResult(null);
        setFdvError(null);
        try {
            setFdvResult(await transferFDVFromAmToCm(project.id));
        } catch (e) {
            // Silence here is what made RUC-306 take a week: the button looked like it worked.
            setFdvError((await extractErrorMessage(e)) ?? 'The transfer failed. Nothing was written.');
        } finally {
            setLoading(false);
        }
    };

    if (!availableDocumentCategories) return <LoadingIndicator />;
    return (
        <div className={theme.modules}>
            <Card variant="flat">
                <h4 className={theme.cardTitle}>Change seller modules</h4>
                <div className="form columns-1 labels-20">
                    <SlideToggle
                        checked={hasModule(BygrModule.ChangeManagement)}
                        disabled={hasModule(BygrModule.ChangeManagement)}
                        onChange={() => setShowDialogCM(true)}>
                        Change management
                    </SlideToggle>
                    <SlideToggle checked={hasModule(BygrModule.Aftermarket)} onChange={() => setShowDialog(true)}>
                        Aftermarket
                    </SlideToggle>
                    <SlideToggle checked={hasModule(BygrModule.QualityManagement)} onChange={() => setShowDialogQM(true)}>
                        Quality management
                    </SlideToggle>
                    <SlideToggle checked={hasModule(BygrModule.Sales)} onChange={() => setShowDialogSales(true)}>
                        Sales
                    </SlideToggle>
                </div>
                {moduleError && <AlertBanner type="danger">{moduleError}</AlertBanner>}
                <Button onClick={() => setModalIsOpen(true)} variant="tertiary" block>
                    Change buyer modules for residential units
                </Button>

                <YesNoDialog
                    title="Are you sure"
                    show={showDialogCM}
                    onClose={() => setShowDialogCM(false)}
                    onAccept={() => toggleModule(BygrModule.ChangeManagement)}
                    body={`Are you sure you want to activate Change management for ${project.name}? This cannot be undone.`}
                />

                <YesNoDialog
                    title="Are you sure"
                    show={showDialog}
                    onClose={() => setShowDialog(false)}
                    onAccept={() => toggleModule(BygrModule.Aftermarket)}
                    body={`Are you sure you want to  ${
                        hasModule(BygrModule.Aftermarket) ? 'deactivate' : 'activate'
                    } aftermarket for ${project.name}?`}
                />

                <YesNoDialog
                    title="Are you sure"
                    show={showDialogQM}
                    onClose={() => setShowDialogQM(false)}
                    onAccept={() => toggleModule(BygrModule.QualityManagement)}
                    body={`Are you sure you want to  ${
                        hasModule(BygrModule.QualityManagement) ? 'deactivate' : 'activate'
                    } QM for ${project.name}?`}
                />

                <YesNoDialog
                    title="Are you sure"
                    show={showDialogSales}
                    onClose={() => setShowDialogSales(false)}
                    onAccept={() => toggleModule(BygrModule.Sales)}
                    body={`Are you sure you want to  ${
                        hasModule(BygrModule.Sales) ? 'deactivate' : 'activate'
                    } Sales for ${project.name}?`}
                />
                <ProjectModulesUnitsModal isOpen={modalIsOpen} projectId={project.id} toggle={() => setModalIsOpen(false)} />
            </Card>

            <Card variant="flat">
                <h4 className={theme.cardTitle}>Transfer between modules</h4>
                <Button loading={loading} onClick={() => setShowDialogFDV(true)} variant="tertiary" block>
                    Transfer FDV from CM options to AM FDV
                </Button>

                {fdvResult && <FdvTransferSummary result={fdvResult} />}
                {fdvError && <AlertBanner type="danger">{fdvError}</AlertBanner>}

                <YesNoDialog
                    title="Are you sure"
                    show={showDialogFDV}
                    onClose={() => setShowDialogFDV(false)}
                    onAccept={() => transfereFDV()}
                    body={`Are you sure you want to transfer documents
                    from CM Options to AM FDV for ${project.name}? This action cannot be undone! So make sure you have selected the right project.`}
                />
            </Card>
            {hasModule(BygrModule.Aftermarket) && (
                <Card variant="flat">
                    <h4 className={theme.cardTitle}>Change document category hierarchy in AM</h4>
                    {availableDocumentCategories.hasLinkedDocuments && (
                        <AlertBanner type="danger">
                            This will change the document category hierarchy and{' '}
                            <b>REMOVE ALL EXISTING DOCUMENT CATEGORIES</b>. This action cannot be undone.
                        </AlertBanner>
                    )}
                    <Button onClick={() => setShowSetCategoryModal(true)} variant="tertiary" block>
                        Change document category hierarchy
                    </Button>
                </Card>
            )}
            <ProjectSelectCategoriesModal
                isOpen={showCategoryModal}
                toogle={() => setShowCategoryModal(false)}
                onSave={async (hierarchyId) => {
                    setShowCategoryModal(false);
                    if (!(await doSetModule(BygrModule.Aftermarket))) {
                        return;
                    }
                    const data = await setDocumentCategoryHierarchy(project.id, hierarchyId);
                    setAvalibleDocumentCategories(data);
                }}
                values={availableDocumentCategories.categories}
                selectedValue={availableDocumentCategories.selectedHierarchy}
            />
            <ProjectSelectCategoriesModal
                isOpen={showSetCategoryModal}
                toogle={() => setShowSetCategoryModal(false)}
                onSave={async (hierarchyId) => {
                    const data = await setDocumentCategoryHierarchy(project.id, hierarchyId);
                    setAvalibleDocumentCategories(data);
                    setShowSetCategoryModal(false);
                }}
                values={availableDocumentCategories.categories}
                selectedValue={availableDocumentCategories.selectedHierarchy}
            />
        </div>
    );
};

export default ProjectModules;
